/**
 * Revenue Comparison Chart
 * 
 * Side-by-side comparison of Actual vs But-For revenue:
 * - Line chart with area fill
 * - Clear loss visualization
 * - Annotations for key events
 * 
 * Designed for expert witness presentations.
 */

import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

interface RevenueDataPoint {
  month: string;
  actual: number;
  butFor: number;
}

interface RevenueComparisonChartProps {
  data: RevenueDataPoint[];
  lossStartDate?: string;
  title?: string;
  width?: number;
  height?: number;
}

export const RevenueComparisonChart: React.FC<RevenueComparisonChartProps> = ({
  data,
  lossStartDate,
  title = "Actual vs But-For Revenue Comparison",
  width = 800,
  height = 400
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 50, right: 100, bottom: 50, left: 80 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .style("font-family", "'Inter', -apple-system, sans-serif");

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Scales
    const xScale = d3.scalePoint()
      .domain(data.map(d => d.month))
      .range([0, innerWidth]);

    const maxValue = d3.max(data, d => Math.max(d.actual, d.butFor)) || 0;
    const yScale = d3.scaleLinear()
      .domain([0, maxValue * 1.1])
      .range([innerHeight, 0]);

    // Area generator for loss visualization
    const lossArea = d3.area<RevenueDataPoint>()
      .x(d => xScale(d.month)!)
      .y0(d => yScale(d.actual))
      .y1(d => yScale(d.butFor))
      .curve(d3.curveMonotoneX);

    // Loss area (difference between but-for and actual)
    g.append("path")
      .datum(data.filter(d => d.butFor > d.actual))
      .attr("fill", "#FEE2E2")
      .attr("opacity", 0.7)
      .attr("d", lossArea);

    // Line generators
    const lineActual = d3.line<RevenueDataPoint>()
      .x(d => xScale(d.month)!)
      .y(d => yScale(d.actual))
      .curve(d3.curveMonotoneX);

    const lineButFor = d3.line<RevenueDataPoint>()
      .x(d => xScale(d.month)!)
      .y(d => yScale(d.butFor))
      .curve(d3.curveMonotoneX);

    // But-For line (dashed)
    g.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "#10B981")
      .attr("stroke-width", 2.5)
      .attr("stroke-dasharray", "8,4")
      .attr("d", lineButFor);

    // Actual line (solid)
    g.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "#3B82F6")
      .attr("stroke-width", 2.5)
      .attr("d", lineActual);

    // Data points - Actual
    g.selectAll(".dot-actual")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "dot-actual")
      .attr("cx", d => xScale(d.month)!)
      .attr("cy", d => yScale(d.actual))
      .attr("r", 4)
      .attr("fill", "#3B82F6")
      .attr("stroke", "white")
      .attr("stroke-width", 2);

    // Data points - But-For
    g.selectAll(".dot-butfor")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "dot-butfor")
      .attr("cx", d => xScale(d.month)!)
      .attr("cy", d => yScale(d.butFor))
      .attr("r", 4)
      .attr("fill", "#10B981")
      .attr("stroke", "white")
      .attr("stroke-width", 2);

    // Loss event marker
    if (lossStartDate) {
      const lossX = xScale(lossStartDate);
      if (lossX !== undefined) {
        g.append("line")
          .attr("x1", lossX)
          .attr("x2", lossX)
          .attr("y1", 0)
          .attr("y2", innerHeight)
          .attr("stroke", "#DC2626")
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", "4,2");

        g.append("text")
          .attr("x", lossX + 8)
          .attr("y", 15)
          .attr("fill", "#DC2626")
          .attr("font-size", "11px")
          .attr("font-weight", "600")
          .text("Loss Event");
      }
    }

    // Calculate total loss
    const totalLoss = data.reduce((sum, d) => sum + Math.max(0, d.butFor - d.actual), 0);

    // Loss annotation
    g.append("text")
      .attr("x", innerWidth / 2)
      .attr("y", innerHeight / 3)
      .attr("text-anchor", "middle")
      .attr("fill", "#DC2626")
      .attr("font-size", "14px")
      .attr("font-weight", "700")
      .text(`Total Lost Revenue: $${d3.format(",.0f")(totalLoss)}`);

    // X Axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .attr("text-anchor", "end")
      .attr("font-size", "10px");

    // Y Axis
    g.append("g")
      .call(d3.axisLeft(yScale)
        .tickFormat(d => `$${d3.format(",.0f")(d as number)}`)
      )
      .selectAll("text")
      .attr("font-size", "10px");

    // Title
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", 25)
      .attr("text-anchor", "middle")
      .attr("fill", "#111827")
      .attr("font-size", "16px")
      .attr("font-weight", "600")
      .text(title);

    // Legend
    const legend = svg.append("g")
      .attr("transform", `translate(${width - margin.right + 10}, ${margin.top + 20})`);

    // Actual legend
    legend.append("line")
      .attr("x1", 0)
      .attr("x2", 20)
      .attr("y1", 0)
      .attr("y2", 0)
      .attr("stroke", "#3B82F6")
      .attr("stroke-width", 2.5);

    legend.append("text")
      .attr("x", 28)
      .attr("y", 4)
      .attr("fill", "#4B5563")
      .attr("font-size", "11px")
      .text("Actual");

    // But-For legend
    legend.append("line")
      .attr("x1", 0)
      .attr("x2", 20)
      .attr("y1", 24)
      .attr("y2", 24)
      .attr("stroke", "#10B981")
      .attr("stroke-width", 2.5)
      .attr("stroke-dasharray", "8,4");

    legend.append("text")
      .attr("x", 28)
      .attr("y", 28)
      .attr("fill", "#4B5563")
      .attr("font-size", "11px")
      .text("But-For");

    // Loss area legend
    legend.append("rect")
      .attr("x", 0)
      .attr("y", 44)
      .attr("width", 20)
      .attr("height", 12)
      .attr("fill", "#FEE2E2")
      .attr("stroke", "#FECACA");

    legend.append("text")
      .attr("x", 28)
      .attr("y", 54)
      .attr("fill", "#4B5563")
      .attr("font-size", "11px")
      .text("Lost Revenue");

  }, [data, lossStartDate, title, width, height]);

  return (
    <div className="revenue-comparison-chart bg-white rounded-lg p-4 shadow-sm border">
      <svg ref={svgRef} />
    </div>
  );
};

export default RevenueComparisonChart;
